import type { TippyProps } from "@tippyjs/react";
import Tippy, { tippy } from "@tippyjs/react";
import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import type { ReferenceElement } from "tippy.js";
import "tippy.js/animations/shift-toward.css";
import "tippy.js/dist/tippy.css";
import "tippy.js/themes/light-border.css";

export default function Tooltip(props: TippyProps) {
  if (!props.content) {
    return <>{props.children}</>;
  } else {
    return (
      <Tippy
        {...{
          theme: "light-border",
          placement: "bottom",
          animation: "shift-toward",
          ...props,
        }}
      />
    );
  }
}

export function SelectorObserverTooltip({
  selector,
  children,
}: {
  selector: string;
  children: ReactNode;
}) {
  const SelectorObserver = useSelectorObserver();
  const rootElementRef = useRef(null);

  useEffect(() => {
    if (!SelectorObserver) return;

    const observer = new SelectorObserver(rootElementRef.current);

    observer.observe(selector, {
      add(el) {
        tippy(el, {
          theme: "light-border",
          placement: "top",
          animation: "shift-toward",
          content: el.getAttribute("title") || "",
        });
      },
      remove(el: ReferenceElement) {
        el._tippy?.destroy();
      },
    });

    return () => {
      try {
        observer.disconnect();
      } catch (err) {
        /** */
      }
    };
  }, [SelectorObserver, selector]);

  return <div ref={rootElementRef}>{children}</div>;
}

interface Constructable<T> {
  new (...args: any): T;
}

function useSelectorObserver() {
  const [observer, setObserver] =
    useState<Constructable<import("selector-observer").default>>();

  useEffect(() => {
    let isCancelled = false;

    import("selector-observer")
      .then((x) => !isCancelled && setObserver(() => x.default))
      .catch((err) => console.error(err));

    return () => {
      isCancelled = true;
    };
  }, []);

  return observer;
}
